import type { UserType } from './Step1UserType';
import getMatchedProductPane from './images/get-matched-product.jpg';
import getMatchedProfessionalPane from './images/get-matched-professional.jpg';
import getMatchedProjectPane from './images/get-matched-project.jpg';

export type EnquiryType = 'professional' | 'project' | 'product';

export type StepKey =
    | 'enquiryType'
    | 'userType'
    | 'region'
    | 'projectType'
    | 'projectScope'
    | 'matchProject'
    | 'productCategory'
    | 'style'
    | 'budget'
    | 'details'
    | 'account'
    | 'done';

export interface UserTypeOption {
    value: UserType;
    title: string;
    subtitle: string;
}

export interface ProjectTypeOption {
    title: string;
    subtitle: string;
}

export interface EnquiryTypeCardOption {
    value: EnquiryType;
    title: string;
    subtitle: string;
}

export interface EnquiryVariantConfig {
    /** Brand-pane statement while the wizard's questions are on the right. */
    paneStatement: string;
    /** Brand-pane photograph — the design gives each variant its own. */
    paneImage: string;
    step1: {
        title: string;
        options: UserTypeOption[];
    };
    step2: {
        title: string;
        helperText?: string;
        /** Project flow lets the user pick several regions to draw references from. */
        multiSelect: boolean;
    };
    step3: {
        title: string;
        options: ProjectTypeOption[];
    };
    /** Productcategory step — only used by the product flow. Null for variants that don't render it. */
    stepProductCategory: {
        title: string;
        /** Fallback options when consumer doesn't pass a live list via props. */
        fallbackOptions: string[];
    } | null;
    step5Budget: {
        title: string;
        helperText: string;
        options: string[];
    };
    step6: {
        title: string;
        /** Label + options for the "Which X are you looking for" select. Null hides the field. */
        lookingForField: {
            label: string;
            options: string[];
        } | null;
        /** Project flow drops the timeline question (browsing intent). */
        showTimeline: boolean;
        descriptionLabel: string;
        descriptionPlaceholder: string;
        timelineLabel: string;
        timelineStartLabel: string;
        timelineEndLabel: string;
    };
    step7: {
        /**
         * Logged-in confirmation copy is rendered as `{prefix}<strong>{email}</strong>{suffix}`.
         * Split into two parts so the rendered email stays bold without string-splitting at render time.
         */
        loggedInConfirmationPrefix: string;
        loggedInConfirmationSuffix: string;
    };
    step8: {
        /** "match you with 2–3 …" — the noun changes per enquiry type. */
        matchSubjectPlural: string;
    };
    /** Per-type step list. `matchProject` is inserted at runtime by the modal. */
    flow: StepKey[];
}

const SHARED_USER_TYPE_OPTIONS: UserTypeOption[] = [
    {
        value: 'Homeowner',
        title: 'Homeowner',
        subtitle: "I'm planning a residential project",
    },
    {
        value: 'Developer',
        title: 'Developer',
        subtitle: "I'm working on a commercial project",
    },
    {
        value: 'Business Owner',
        title: 'Business Owner',
        subtitle: 'I need a fit-out, office or hospitality space',
    },
    {
        value: 'Industry Professional',
        title: 'Industry Professional',
        subtitle: 'Im looking to add someone to my Project Team',
    },
];

/** Project-scale budget brackets used by the professional and project variants. */
const PROJECT_BUDGET_OPTIONS = [
    'Under $50k',
    '$50k - $100k',
    '$100k - $250k',
    '$250k - $500k',
    '$500k - $1m',
    '$1m - $2m',
    '$2m - $3m',
    '$3m - $5m',
    '$5m+',
    'Not sure yet',
];

/** Product-scale budget brackets sized for typical product purchases. */
const PRODUCT_BUDGET_OPTIONS = [
    'Under $100',
    '$100 - $500',
    '$500 - $1k',
    '$1k - $2.5k',
    '$2.5k - $5k',
    '$5k - $10k',
    '$10k+',
    'Not sure yet',
];

const PROJECT_DESCRIPTION_PLACEHOLDER =
    "e.g. We're planning a 4-bedroom new build on a hillside site in Herne Bay. We want strong connection to the harbour, open-plan living, and a calm, minimal aesthetic. We have planning consent in progress…";

const PRODUCT_DESCRIPTION_PLACEHOLDER =
    'e.g. Looking for matte black tapware to suit a coastal-style kitchen. Need ~4 mixers and 2 wall-mounted spouts. Open to NZ-made suppliers, want delivery within 6 weeks…';

const SHARED_PROJECT_TYPE_OPTIONS: ProjectTypeOption[] = [
    { title: 'New Build', subtitle: 'Building from the ground up' },
    {
        title: 'Renovation',
        subtitle: 'Transforming or modernising an existing project',
    },
    { title: 'Extension', subtitle: 'Adding space to what you already have' },
    {
        title: 'Interior Design',
        subtitle: 'Interior space, fit-out or decoration only',
    },
    {
        title: 'Commercial',
        subtitle: 'Office, hospitality, retail or development project',
    },
    { title: 'Landscaping', subtitle: 'Outdoor or garden project' },
];

const PROFESSIONAL_FLOW: StepKey[] = [
    'userType',
    'region',
    'projectType',
    'projectScope',
    'style',
    'budget',
    'details',
    'account',
    'done',
];

// Lean inspiration brief — user is browsing references, not hiring or buying.
const PROJECT_FLOW: StepKey[] = [
    'projectType',
    'style',
    'region',
    'details',
    'account',
    'done',
];

// Product-in-project context — user is shopping, with light project framing.
const PRODUCT_FLOW: StepKey[] = [
    'userType',
    'region',
    'projectType',
    'productCategory',
    'budget',
    'details',
    'account',
    'done',
];

/**
 * Brand-pane statement for the type-selector step, where no variant has been
 * picked yet — a variant's own line would pitch professionals while the cards
 * still offer projects and products.
 */
export const SELECTOR_PANE_STATEMENT =
    "Answer a few quick questions and we'll match you with the right people and products for your project.";

export const ENQUIRY_CONFIG: Record<EnquiryType, EnquiryVariantConfig> = {
    professional: {
        paneImage: getMatchedProfessionalPane,
        paneStatement:
            "Answer a few quick questions and we'll match you with the right professionals for your project.",
        step1: {
            title: 'First, please tell us who you are.',
            options: SHARED_USER_TYPE_OPTIONS,
        },
        step2: {
            title: 'Where is your project located?',
            multiSelect: false,
        },
        step3: {
            title: 'Select the option that best describes your project',
            options: SHARED_PROJECT_TYPE_OPTIONS,
        },
        stepProductCategory: null,
        step5Budget: {
            title: "What's your budget range?",
            helperText:
                'Approximate figures help us find practices suited to your project scope.',
            options: PROJECT_BUDGET_OPTIONS,
        },
        step6: {
            title: 'Tell us a bit more about your project',
            lookingForField: {
                label: 'Which professionals are you looking for',
                // TODO(BC-4176): replace placeholder professional list with real category data
                options: ['Architects', 'Builders', 'Designers'],
            },
            showTimeline: true,
            descriptionLabel: 'Project description',
            descriptionPlaceholder: PROJECT_DESCRIPTION_PLACEHOLDER,
            timelineLabel: 'Project timeline',
            timelineStartLabel: 'Start date',
            timelineEndLabel: 'End date',
        },
        step7: {
            loggedInConfirmationPrefix: "We'll send your matched results to ",
            loggedInConfirmationSuffix:
                '. Your details are only shared with the architects if you decide to contact them — never sold or used for marketing without your consent.',
        },
        step8: {
            matchSubjectPlural: 'professionals',
        },
        flow: PROFESSIONAL_FLOW,
    },
    project: {
        paneImage: getMatchedProjectPane,
        paneStatement:
            "Answer a few quick questions and we'll match you with the right inspiration for your project.",
        // step1/step5Budget/step6.timeline* are populated for type completeness;
        // the project flow doesn't render any of them.
        step1: {
            title: 'First, please tell us who you are.',
            options: SHARED_USER_TYPE_OPTIONS,
        },
        step2: {
            title: 'Which regions should we pull projects from?',
            helperText:
                "Select up to 3 regions - we'll find and recommend projects from each.",
            multiSelect: true,
        },
        step3: {
            title: 'What kind of project are you looking for?',
            options: SHARED_PROJECT_TYPE_OPTIONS,
        },
        stepProductCategory: null,
        step5Budget: {
            title: "What's your budget range?",
            helperText: '',
            options: PROJECT_BUDGET_OPTIONS,
        },
        step6: {
            title: 'Help us understand what you are looking for',
            lookingForField: {
                label: 'Which rooms or spaces are you focused on?',
                options: [
                    'Kitchen',
                    'Living',
                    'Bedroom',
                    'Bathroom',
                    'Outdoor',
                    'Whole home',
                ],
            },
            showTimeline: false,
            descriptionLabel: 'Project description',
            descriptionPlaceholder: PROJECT_DESCRIPTION_PLACEHOLDER,
            timelineLabel: 'Project timeline',
            timelineStartLabel: 'Start date',
            timelineEndLabel: 'End date',
        },
        step7: {
            loggedInConfirmationPrefix:
                "We'll send your inspiration shortlist to ",
            loggedInConfirmationSuffix:
                '. Your details are only shared with the project owners if you decide to follow up — never sold or used for marketing without your consent.',
        },
        step8: {
            matchSubjectPlural: 'inspiring projects',
        },
        flow: PROJECT_FLOW,
    },
    product: {
        paneImage: getMatchedProductPane,
        paneStatement:
            "Answer a few quick questions and we'll match you with the right products for your project.",
        step1: {
            title: 'First, please tell us who you are.',
            options: SHARED_USER_TYPE_OPTIONS,
        },
        step2: {
            title: 'Where do you need the products delivered?',
            multiSelect: false,
        },
        step3: {
            title: 'What kind of project is this product for?',
            options: SHARED_PROJECT_TYPE_OPTIONS,
        },
        stepProductCategory: {
            title: 'What kind of product are you looking for?',
            fallbackOptions: [
                'Building materials',
                'Fixtures & fittings',
                'Furniture',
                'Lighting',
                'Appliances',
            ],
        },
        step5Budget: {
            title: "What's your budget range?",
            helperText:
                'Approximate figures help us shortlist suppliers in your range.',
            options: PRODUCT_BUDGET_OPTIONS,
        },
        step6: {
            title: 'Tell us more about what you need',
            lookingForField: null,
            showTimeline: true,
            descriptionLabel: 'Product description',
            descriptionPlaceholder: PRODUCT_DESCRIPTION_PLACEHOLDER,
            timelineLabel: 'Delivery timeline',
            timelineStartLabel: 'Expected date',
            timelineEndLabel: 'Latest acceptable date',
        },
        step7: {
            loggedInConfirmationPrefix: "We'll send your product matches to ",
            loggedInConfirmationSuffix:
                '. Your details are only shared with the suppliers if you decide to contact them — never sold or used for marketing without your consent.',
        },
        step8: {
            matchSubjectPlural: 'products and suppliers',
        },
        flow: PRODUCT_FLOW,
    },
};

export const ENQUIRY_TYPE_SELECTOR_OPTIONS: EnquiryTypeCardOption[] = [
    {
        value: 'project',
        title: 'Projects',
        subtitle: 'Find projects that match your brief',
    },
    {
        value: 'product',
        title: 'Products',
        subtitle: 'Find products and suppliers for your project',
    },
    {
        value: 'professional',
        title: 'Professionals',
        subtitle: 'Find architects, builders, designers',
    },
];
